// Cosmetic catalog (§7.2, §7.3, §17.5). 68 items; ids are stable per category and CATALOG is
// ordered by category, then id. Hat id 0 means "no hat" and is not a catalog item.
import { randInt, type RngHolder } from './rng';

export interface Cosmetics { color: number; pattern: number; hat: number; eyes: number; honk: number; trail: number; scream: number; celebration: number; identityPref: number /*0-7 or 255*/ }
export type CosmeticCategory = keyof Omit<Cosmetics, 'identityPref'>;
export interface CatalogItem {
  cat: CosmeticCategory;
  id: number;
  name: string;
  unlockLevel: number /*1 starter, 2..40, 0 shop-only*/;
  price: number;
  /** Stable code key used by renderer/audio recipes (e.g. 'partyCone', 'clownHorn'). */
  key: string;
  /** Body colors only: sRGB hex. */
  hex?: string;
}

export const COSMETIC_CATEGORIES: readonly CosmeticCategory[] = ['color', 'pattern', 'hat', 'eyes', 'honk', 'trail', 'scream', 'celebration'];
export const HAT_NONE = 0;
export const NO_IDENTITY_PREF = 255;

// ---- Match identity (§7.2, §12.2) ------------------------------------------------------------

export type Glyph = 'circle' | 'triangle' | 'square' | 'star' | 'heart' | 'diamond' | 'moon' | 'bolt';
export interface Identity { name: string; hex: string; glyph: Glyph }

/** Identity per index 0–7; identity colors double as body colors 0–7. */
export const IDENTITIES: readonly Identity[] = [
  { name: 'Tomato', hex: '#FF5A4E', glyph: 'circle' },
  { name: 'Sunflower', hex: '#FFC93C', glyph: 'triangle' },
  { name: 'Mint', hex: '#3DDC97', glyph: 'square' },
  { name: 'Sky', hex: '#4CB8FF', glyph: 'star' },
  { name: 'Grape', hex: '#9B6BFF', glyph: 'heart' },
  { name: 'Bubblegum', hex: '#FF7AC8', glyph: 'diamond' },
  { name: 'Tangerine', hex: '#FF9A3C', glyph: 'moon' },
  { name: 'Snow', hex: '#F2F4FF', glyph: 'bolt' },
];

/** "Colorblind: on" identity palette, by identity index (§12.2). Glyphs are always drawn. */
export const COLORBLIND_IDENTITY_HEX: readonly string[] = ['#E69F00', '#56B4E9', '#009E73', '#F0E442', '#0072B2', '#D55E00', '#CC79A7', '#FFFFFF'];

/** Team ring styles by `Bean.team` (1 red, 2 blue). */
export const TEAM_STYLES: Readonly<Record<1 | 2, { name: string; hex: string; glyph: Glyph }>> = {
  1: { name: 'Red', hex: '#FF5A4E', glyph: 'square' },
  2: { name: 'Blue', hex: '#4CB8FF', glyph: 'circle' },
};

/** Fixed emotes, by emote id 1–4 (index 0 unused). */
export const EMOTES: readonly string[] = ['', 'wave', 'dance', 'laugh', 'flex'];

// ---- Catalog ----------------------------------------------------------------------------------

type ItemSpec = readonly [key: string, name: string, hex?: string];

const ITEMS: Readonly<Record<CosmeticCategory, readonly ItemSpec[]>> = {
  color: [
    ['tomato', 'Tomato', '#FF5A4E'], ['sunflower', 'Sunflower', '#FFC93C'], ['mint', 'Mint', '#3DDC97'], ['sky', 'Sky', '#4CB8FF'],
    ['grape', 'Grape', '#9B6BFF'], ['bubblegum', 'Bubblegum', '#FF7AC8'], ['tangerine', 'Tangerine', '#FF9A3C'], ['snow', 'Snow', '#F2F4FF'],
    ['lavender', 'Lavender', '#C7B8FF'], ['teal', 'Teal', '#2BB3A3'], ['peach', 'Peach', '#FFB59A'], ['lime', 'Lime', '#B6E35A'],
    ['navy', 'Navy', '#2E4A8F'], ['charcoal', 'Charcoal', '#4A4458'], ['gold', 'Gold', '#E8B64C'], ['rose', 'Rose', '#E86A8E'],
  ],
  pattern: [
    ['solid', 'Solid'], ['stripes', 'Stripes'], ['polka', 'Polka'], ['checker', 'Checker'],
    ['gradient', 'Gradient'], ['galaxy', 'Galaxy'], ['camo', 'Camo'], ['tieDye', 'Tie-Dye'],
  ],
  // Hat ids start at 1 (0 = none).
  hat: [
    ['partyCone', 'Party Cone'], ['propellerBeanie', 'Propeller Beanie'], ['topHat', 'Top Hat'], ['trafficCone', 'Traffic Cone'],
    ['chefToque', 'Chef Toque'], ['vikingHelmet', 'Viking Helmet'], ['duckFloatie', 'Duck Floatie'], ['cowboyHat', 'Cowboy Hat'],
    ['wizardHat', 'Wizard Hat'], ['flowerCrown', 'Flower Crown'], ['fishbowl', 'Fishbowl'], ['catEars', 'Cat Ears'],
    ['halo', 'Halo'], ['mohawk', 'Mohawk'], ['pirateTricorn', 'Pirate Tricorn'], ['paperCrown', 'Paper Crown'],
  ],
  eyes: [
    ['classic', 'Classic Googly'], ['giant', 'Giant'], ['tiny', 'Tiny'], ['sleepy', 'Sleepy'], ['angryBrows', 'Angry Brows'], ['lashes', 'Lashes'],
  ],
  honk: [
    ['clownHorn', 'Clown Horn'], ['duck', 'Duck'], ['kazoo', 'Kazoo'], ['foghorn', 'Foghorn'],
    ['bikeBell', 'Bike Bell'], ['squeaky', 'Squeaky Toy'], ['goose', 'Goose'], ['airhorn', 'Airhorn'],
  ],
  trail: [
    ['none', 'None'], ['sparkles', 'Sparkles'], ['bubbles', 'Bubbles'], ['hearts', 'Hearts'], ['musicNotes', 'Music Notes'], ['rainbow', 'Rainbow'],
  ],
  scream: [
    ['slideWhistle', 'Slide Whistle'], ['opera', 'Opera Note'], ['yodel', 'Yodel'], ['dopplerNooo', 'Doppler "NOOO"'],
  ],
  celebration: [
    ['confettiCannon', 'Confetti Cannon'], ['fireworks', 'Fireworks'], ['flowerBurst', 'Flower Burst'], ['discoBall', 'Disco Ball'],
  ],
};

const FIRST_ID: Readonly<Record<CosmeticCategory, number>> = { color: 0, pattern: 0, hat: 1, eyes: 0, honk: 0, trail: 0, scream: 0, celebration: 0 };

/**
 * The 20 level 1 starters (§7.3): the 18 named ones (8 identity colors, solid, party cone, propeller,
 * classic, giant, clown horn, duck, no trail, slide whistle, confetti cannon) plus tie-dye and the
 * Doppler "NOOO", a second choice of pattern and of fall scream.
 */
const STARTERS: readonly string[] = [
  'color:tomato', 'color:sunflower', 'color:mint', 'color:sky', 'color:grape', 'color:bubblegum', 'color:tangerine', 'color:snow',
  'pattern:solid', 'hat:partyCone', 'hat:propellerBeanie', 'eyes:classic', 'eyes:giant', 'honk:clownHorn', 'honk:duck',
  'trail:none', 'scream:slideWhistle', 'celebration:confettiCannon',
  'pattern:tieDye', 'scream:dopplerNooo',
];

/** Bean Level 2–40 unlock order (index 0 = level 2), interleaved across categories. */
const LEVEL_ORDER: readonly string[] = [
  'color:lavender', 'pattern:stripes', 'hat:topHat', 'honk:kazoo', 'trail:sparkles', 'eyes:tiny', 'color:teal', 'hat:trafficCone',
  'scream:opera', 'pattern:polka', 'honk:squeaky', 'color:peach', 'hat:catEars', 'celebration:fireworks', 'trail:bubbles', 'pattern:checker',
  'eyes:sleepy', 'hat:chefToque', 'color:lime', 'honk:bikeBell', 'scream:yodel', 'hat:cowboyHat', 'trail:hearts', 'pattern:gradient',
  'color:navy', 'hat:vikingHelmet', 'honk:goose', 'celebration:flowerBurst', 'eyes:angryBrows', 'hat:duckFloatie', 'color:charcoal', 'pattern:camo',
  'trail:musicNotes', 'hat:flowerCrown', 'honk:foghorn', 'color:rose', 'hat:mohawk', 'hat:pirateTricorn', 'hat:paperCrown',
];

/** The 9 shop-only items and their Bonk Bucks price tier (150 / 300 / 500). */
const SHOP_PRICES: Readonly<Record<string, number>> = {
  'eyes:lashes': 150, 'honk:airhorn': 150, 'hat:fishbowl': 150,
  'hat:wizardHat': 300, 'color:gold': 300, 'hat:halo': 300,
  'pattern:galaxy': 500, 'trail:rainbow': 500, 'celebration:discoBall': 500,
};

function buildCatalog(): CatalogItem[] {
  const out: CatalogItem[] = [];
  for (const cat of COSMETIC_CATEGORIES) {
    ITEMS[cat].forEach(([key, name, hex], i) => {
      const ref = `${cat}:${key}`;
      const level = LEVEL_ORDER.indexOf(ref);
      const item: CatalogItem = {
        cat, id: FIRST_ID[cat] + i, name, key,
        unlockLevel: STARTERS.includes(ref) ? 1 : level >= 0 ? level + 2 : 0,
        price: SHOP_PRICES[ref] ?? 0,
      };
      if (hex !== undefined) item.hex = hex;
      out.push(item);
    });
  }
  return out;
}

/** 68 entries per §7.3, fixed order = id order within category. */
export const CATALOG: CatalogItem[] = buildCatalog();

/** Ids valid for a category (hat additionally accepts HAT_NONE). */
export function categoryIds(cat: CosmeticCategory): number[] {
  const ids = CATALOG.filter((it) => it.cat === cat).map((it) => it.id);
  return cat === 'hat' ? [HAT_NONE, ...ids] : ids;
}

export function catalogItem(cat: CosmeticCategory, id: number): CatalogItem | undefined {
  return CATALOG.find((it) => it.cat === cat && it.id === id);
}

export function findCatalogItem(cat: CosmeticCategory, key: string): CatalogItem | undefined {
  return CATALOG.find((it) => it.cat === cat && it.key === key);
}

/** Index into CATALOG (the encoding of `profile.owned`), or −1. */
export function catalogIndex(cat: CosmeticCategory, id: number): number {
  return CATALOG.findIndex((it) => it.cat === cat && it.id === id);
}

export const DEFAULT_COSMETICS: Cosmetics = {
  color: 0, pattern: 0, hat: 1, eyes: 0, honk: 0, trail: 0, scream: 0, celebration: 0, identityPref: NO_IDENTITY_PREF,
};

function isValidId(cat: CosmeticCategory, v: unknown): v is number {
  if (typeof v !== 'number' || !Number.isInteger(v)) return false;
  return (cat === 'hat' && v === HAT_NONE) || catalogItem(cat, v) !== undefined;
}

/** Clamps unknown ids to defaults (server uses this on every client-supplied Cosmetics). */
export function validateCosmetics(c: unknown): Cosmetics {
  const src = (typeof c === 'object' && c !== null ? c : {}) as Record<string, unknown>;
  const out: Cosmetics = { ...DEFAULT_COSMETICS };
  for (const cat of COSMETIC_CATEGORIES) {
    const v = src[cat];
    if (isValidId(cat, v)) out[cat] = v;
  }
  const pref = src.identityPref;
  if (typeof pref === 'number' && Number.isInteger(pref) && pref >= 0 && pref < IDENTITIES.length) out.identityPref = pref;
  return out;
}

/** Random look from the full catalog (bots, §10.5). Hats are never "none". */
export function randomCosmetics(h: RngHolder): Cosmetics {
  const out: Cosmetics = { ...DEFAULT_COSMETICS };
  for (const cat of COSMETIC_CATEGORIES) {
    const ids = CATALOG.filter((it) => it.cat === cat);
    out[cat] = ids[randInt(h, ids.length)].id;
  }
  return out;
}
